import {
	type Component,
	type ComponentProps,
	type JSXElement,
	onMount,
	Show,
	splitProps
} from "solid-js";
import { tv, type VariantProps } from "tailwind-variants";
import type { moleculesElementsSizes } from "~/solidJs/shared/helpers/types/MoleculesElementsSizes";

type inputVariants = VariantProps<typeof styles>["color"];
type InputOwnProps = {
	size?: moleculesElementsSizes;
	className?: string;
	variant?: inputVariants;
	disabled?: boolean;
};
const styles = tv({
	base: "w-full h-max rounded-md border border-solid text-current focus:outline-none focus:placeholder-opacity-0 invalid:outline invalid:outline-red-500 transition-opacity placeholder-text-text-secondary placeholder:font-medium",
	defaultVariants: {
		color: "default",
		size: "small"
	},
	variants: {
		color: {
			default: "bg-white border-gray-300",
			trainPageVariant: "bg-gray-800 border border-gray-300 text-white",
			outline:
				"border-border-gray-dark placeholder:text-text-secondary rounded-lg border bg-transparent",
			dropDownBtn:
				"bg-transparent border-transparent border-gray-300 border text-white w-max p-2 h-max",
			Gray: "ring-[1px] ring-border-tertiary border-none bg-background-gray-light placeholder:text-text-secondary text-text-primary text-14-medium focus:ring-background-violet focus:ring-[2px]"
		},
		size: {
			small: "px-4 py-2 text-2xs font-medium",
			medium: "px-4 py-3 text-sm font-medium rounded-xl",
			large: "px-6 py-4 text-2xs font-medium"
		},
		disabled: {
			true: "pointer-events-none opacity-50"
		}
	}
});
export type InputProps = InputOwnProps & Omit<ComponentProps<"input">, keyof InputOwnProps>;

export const Input: Component<InputProps> = (props) => {
	const [notNativeAttributes, others] = splitProps(props, [
		"class",
		"className",
		"size",
		"variant",
		"disabled",
		"ref"
	]);

	let inputRef: HTMLInputElement;

	onMount(() => {
		inputRef.addEventListener("keydown", (e) => e.stopPropagation());

		if (typeof notNativeAttributes.ref === "function") {
			notNativeAttributes.ref(inputRef);
		}
	});

	return (
		<input
			class={styles({
				size: props.size,
				color: props.variant,
				class: props.class || props.className,
				disabled: props.disabled
			})}
			ref={(ref) => {
				inputRef = ref;
				if (typeof notNativeAttributes.ref === "function") {
					notNativeAttributes.ref(ref);
				}
			}}
			disabled={notNativeAttributes.disabled}
			{...others}
		/>
	);
};

type InputLabelProps = {
	children: JSXElement;
	id: string;
	labelContent: JSXElement;
};

export const InputLabel: Component<InputLabelProps> = (props) => {
	return (
		<div class="flex flex-col gap-2 w-full">
			<label for={props.id} class="text-14-medium text-text-secondary-v2">
				{props.labelContent}
			</label>
			{props.children}
		</div>
	);
};

type InputErrorProps = {
	children: JSXElement;
	id: string;
	labelContent: JSXElement;
	errorMessage?: string;
};

export const InputLabelError: Component<InputErrorProps> = (props) => {
	return (
		<div class="flex flex-col gap-2 w-full">
			<label for={props.id} class="text-14-medium text-text-secondary-v2">
				{props.labelContent}
			</label>
			<div class="flex flex-col gap-0.5 w-full">
				{props.children}
				<Show when={props.errorMessage}>
					<span class="label-10-medium text-background-orange">{props.errorMessage}</span>
				</Show>
			</div>
		</div>
	);
};
